
import { Observable, throwError as observableThrowError, of as observableOf } from 'rxjs';
import { delay, flatMap } from 'rxjs/operators';
import {
    Headers,
    Response
} from 'app/services/http';
import { formatStringForCompare } from './string.util';
import { sleep } from './sleep.util';
import { ThrottleStateManager } from './throttle-state-manager';

export const VSTS_RESOURCE_ID = '499b84ac-1321-427f-aa17-267ca6975798';

const httpStatusCodes: Map<string, number> = new Map([
    ['Unknown', 0],
    ['Continue', 100],
    ['Switching Protocols', 101],
    ['Processing', 102],
    ['OK', 200],
    ['Created', 201],
    ['Accepted', 202],
    ['Non Authoritative Information', 203],
    ['No Content', 204],
    ['Reset Content', 205],
    ['Partial Content', 206],
    ['Multi-Status', 207],
    ['Multiple Choices', 300],
    ['Moved Permanently', 301],
    ['Moved Temporarily', 302],
    ['See Other', 303],
    ['Not Modified', 304],
    ['Use Proxy', 305],
    ['Temporary Redirect', 307],
    ['Permanent Redirect', 308],
    ['Bad Request', 400],
    ['Unauthorized', 401],
    ['Forbidden', 403],
    ['Not Found', 404],
    ['Method Not Allowed', 405],
    ['Not Acceptable', 406],
    ['Proxy Authentication Required', 407],
    ['Request Timeout', 408],
    ['Conflict', 409],
    ['Gone', 410],
    ['Length Required', 411],
    ['Precondition Failed', 412],
    ['Request Entity Too Large', 413],
    ['Request-URI Too Long', 414],
    ['Unsupported Media Type', 415],
    ['Requested Range Not Satisfiable', 416],
    ['Expectation Failed', 417],
    [`I'm a teapot`, 418],
    ['Insufficient Space on Resource', 419],
    ['Method Failure', 420],
    ['Unprocessable Entity', 422],
    ['Locked', 423],
    ['Failed Dependency', 424],
    ['Precondition Required', 428],
    ['Too Many Requests', 429],
    ['Request Header Fields Too Large', 431],
    ['Server Error', 500],
    ['Not Implemented', 501],
    ['Bad Gateway', 502],
    ['Service Unavailable', 503],
    ['Gateway Timeout', 504],
    ['HTTP Version Not Supported', 505],
    ['Insufficient Storage', 507],
    ['Network Authentication Required', 511]
]);

export function getHeadersForGitHub(token: string): Headers {
    const headers: Headers = new Headers();

    headers.append('Authorization', `Bearer ${token}`);
    headers.append('Content-type', 'application/json');

    return headers;
}
export interface IRetryPolicyOptions {
    maxRetries?: number;
    initialDelayMs?: number;
    maxDelayMs?: number;
    retryableStatusCodes?: number[];
    useExponentialBackoff?: boolean;
}

export function createRetryPolicy(options: IRetryPolicyOptions = {}, throttleManager?: ThrottleStateManager): (error: Observable<Response>) => Observable<Response> {
    const {
        maxRetries = 3,
        initialDelayMs = 500,
        maxDelayMs = 30000,
        retryableStatusCodes = [503, 504, 0, 429],
        useExponentialBackoff = false
    } = options;

    return (error: Observable<Response>): Observable<Response> => {
        let retryCount = 0;
        return error.pipe(
            flatMap((errorResp: Response) => {
                if (retryCount < maxRetries && retryableStatusCodes.includes(errorResp.status)) {
                    let delayTime = initialDelayMs;

                    if (errorResp.status === 429) {
                        // Notify throttle manager that we're being throttled
                        if (throttleManager) {
                            throttleManager.reportThrottle();
                        }

                        // Check Fabric API header first (simple seconds format)
                        const retryAfter = errorResp.headers.get('Retry-After');
                        if (retryAfter) {
                            delayTime = +retryAfter * 1000;
                        } else {
                            // Check ARM API header (HH:MM:SS format)
                            const waitTime = errorResp.headers.get('x-ms-user-quota-resets-after');
                            if (waitTime) {
                                delayTime = +(waitTime.split(':')[2]) * 1000;
                            } else if (useExponentialBackoff) {
                                // Fallback to exponential backoff if no header present
                                delayTime = Math.min(initialDelayMs * Math.pow(2, retryCount), maxDelayMs);
                            }
                        }
                    } else if (useExponentialBackoff) {
                        delayTime = Math.min(initialDelayMs * Math.pow(2, retryCount), maxDelayMs);
                    }

                    retryCount++;
                    return observableOf(errorResp).pipe(delay(delayTime));
                }
                return observableThrowError(errorResp);
            }));
    };
}

export function defaultRetryPolicy(error: Observable<Response>): Observable<Response> {
    let retryCount: number = 3;
    let delayTime: number = 500;
    return error.pipe(
        flatMap((errorResp: Response) => {
            if (retryCount-- > 0 && (errorResp.status === 503 || errorResp.status === 504 || errorResp.status === 0 || errorResp.status === 429)) {
                if (errorResp.status === 429) {
                    const waitTime = errorResp.headers.get('x-ms-user-quota-resets-after');
                    if (waitTime) {
                        delayTime = +(waitTime.split(':')[2]) * 1000;
                    }
                }
                return observableOf(errorResp).pipe(delay(delayTime));
            }
            return observableThrowError(errorResp);
        }));
}

export function retryPolicyForGitHubRateLimits(error: Observable<Response>): Observable<Response> {
    let retryCount: number = 3;
    let delayTime: number = 500;
    return error.pipe(
        flatMap((errorResp: Response) => {
            if (retryCount-- > 0 && (errorResp.status === 503 || errorResp.status === 504 || errorResp.status === 0
                || errorResp.status === 429 || errorResp.status === 403)) {
                if (errorResp.status === 429) {
                    const waitTime = errorResp.headers.get('x-ms-user-quota-resets-after');
                    if (waitTime) {
                        delayTime = +(waitTime.split(':')[2]) * 1000;
                    }
                } else if (errorResp.status === 403 && errorResp.headers.get('Retry-After')) {
                    // Hits GitHub secondary rate-limits for multiple concurrent calls or using 60 seconds CPU timer in a minute
                    // https://docs.github.com/en/rest/overview/resources-in-the-rest-api?apiVersion=2022-11-28#secondary-rate-limits
                    delayTime = +(errorResp.headers.get('Retry-After')) * 1000;
                }
                return observableOf(errorResp).pipe(delay(delayTime));
            }
            return observableThrowError(errorResp);
        }));
}

export function retryPolicyForSasTokenTimeFrameError(error: Observable<Response>): Observable<Response> {
    // Some browsers are a little bit out of sync with the server (seconds behind, usually 1-2), this causes the request to fail because the token signature start time.
    let retryCount: number = 3;
    const invalidTimeFrameError = 'signature not valid in the specified time frame';
    return error.pipe(
        flatMap((errorResp: Response) => {
            if (retryCount-- > 0 && errorResp.status === 403 && errorResp.text().toLowerCase().includes(invalidTimeFrameError)) {
                return observableOf(errorResp).pipe(delay(2000));
            }
            return observableThrowError(errorResp);
        }));
}

export function retryPolicyForBadRequest(error: Observable<Response>): Observable<Response> {
    let retryCount: number = 3;
    return error.pipe(
        flatMap((errorResp: Response) => {
            if (retryCount-- > 0 && (errorResp.status === 400 || errorResp.status === 503 || errorResp.status === 504)) {
                return observableOf(errorResp).pipe(delay(2000));
            }
            return observableThrowError(errorResp);
        }));
}

export function retryPolicyForConflictAndServerError(error: Observable<Response>): Observable<Response> {
    let retryCount: number = 3;
    return error.pipe(
        flatMap((errorResp: Response) => {
            if (retryCount-- > 0 && (errorResp.status === 409 || errorResp.status === 503 || errorResp.status === 504)) {
                return observableOf(errorResp).pipe(delay(3000));
            }
            return observableThrowError(errorResp);
        }));
}

export function getStatusCodeByName(httpStatus: string): number {
    if (!httpStatus) {
        return -1;
    }
    if (httpStatusCodes.has(httpStatus)) {
        return httpStatusCodes.get(httpStatus);
    }
    for (const [key] of httpStatusCodes) {
        if (formatStringForCompare(key) === formatStringForCompare(httpStatus)) {
            return httpStatusCodes.get(key);
        }
    }
    return -1;
}

type PaginationParams<R> = {
    pageSize: number;
    fn: (pageSize: number, offset: number) => Promise<R[]>;
    timeout?: number;
    maxRetries?: number;
    retryDelay?: number
};
export async function paginate<R>({
    pageSize,
    fn,
    maxRetries = 3,
    retryDelay = 0,
    timeout = 30 * 1000
}: PaginationParams<R>): Promise<R[]> {
    let offset = 0;
    let isFinished = false;
    const result: R[] = [];
    let retries = 0;

    while (!isFinished) {
        try {
            await sleep(0);
            const response = await Promise.race([
                fn(pageSize, offset),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), timeout))
            ]) as Awaited<ReturnType<PaginationParams<R>['fn']>>;
            if (response.length < pageSize) {
                isFinished = true;
            }
            offset += response.length;
            result.push(...response);
            retries = 0;
        } catch (error) {
            if (retries < maxRetries) {
                retries++;
                if (retryDelay > 0) {
                    await sleep(retryDelay);
                }
            } else {
                throw error;
            }
        }
    }

    return result;
}


type PollParams<R> = {
    fn: () => R | Promise<R>;
    timeout?: number;
    interval?: number;
    stopWhen?: (pollResult: R) => Promise<boolean> | boolean;
    signal?: AbortSignal;
};

export function poll<R = unknown>({
    timeout = 60 * 1000,
    interval = 10 * 1000,
    stopWhen = Boolean,
    signal,
    fn
}: PollParams<R>): Promise<R> {
    const start = Date.now();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const execute: any = async (resolve: typeof Promise.resolve, reject: typeof Promise.reject) => {
        const roundStart = Date.now();
        const isTimeout: boolean = roundStart - start > timeout;

        if (isTimeout) {
            reject('Polling timeout: > ' + timeout);
        } else if (signal?.aborted) {
            resolve();
        } else {
            try {
                const pollResult = await Promise.resolve(fn());
                const stop: boolean = stopWhen ? await stopWhen(pollResult) : false;
                if (stop) {
                    resolve(pollResult);
                } else {
                    const nextInterval = Math.max(interval - (Date.now() - roundStart), 0);
                    setTimeout(() => execute(resolve, reject), nextInterval);
                }
            } catch (ex) {
                reject(ex);
            }
        }
    };

    return new Promise(execute);
}